import { get } from "lodash";
import { DateTime } from "luxon";
import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import toast from "react-hot-toast";
import { ClientContext } from "../App";
import DateRangePicker from "../components/DateRangePicker";
import LoadingSpinner from "../components/LoadingSpinner";
import PlatformAnalyticsTable from "../components/PlatformAnalyticsTable";
import {
  getClientMetrics,
  getPlatformMetrics,
} from "../lib/automatic";

/**
 * MetricCard component for displaying individual metric values
 * @param {string} title - The title of the metric
 * @param {string|number} value - The value to display
 * @param {string} subtitle - Optional subtitle text
 * @param {number} percentChange - Optional percent change to display as a badge
 * @returns {JSX.Element} A styled metric card component
 */
const MetricCard = ({ title, value, subtitle, percentChange }) => {
  // Only render the badge if percentChange is provided
  const showBadge = percentChange !== undefined && percentChange !== null;

  // Determine badge styling based on positive or negative change
  const badgeColor =
    percentChange >= 0
      ? "bg-green-100 text-green-800"
      : "bg-red-100 text-red-800";

  // Get the appropriate arrow based on direction
  const arrow = percentChange >= 0 ? "↑" : "↓";

  return (
    <div className="border border-gray-200 hover:border-black rounded p-4 flex-shrink transition-colors duration-100">
      <div className="flex flex-col gap-y-1">
        <div className="flex justify-between items-center">
          <p className="text-base font-semibold">{title}</p>
          {showBadge && (
            <div
              className={`text-xs px-2 py-1 rounded-full ${badgeColor} font-medium flex items-center`}
            >
              <span className="mr-0.5">{arrow}</span>
              {formatPercentChange(percentChange)}
            </div>
          )}
        </div>
        <p className="text-2xl font-bold">{value}</p>
        {subtitle && <p className="text-sm text-gray-600 mt-1">{subtitle}</p>}
      </div>
    </div>
  );
};

/**
 * Format a number with thousands separators
 * @param {number} num - Number to format
 * @returns {string} Formatted number string with commas
 */
const formatNumber = (num) => {
  return num?.toLocaleString() || "0";
};

/**
 * Format a decimal as a percentage
 * @param {number} decimal - Decimal value (e.g., 0.75)
 * @returns {string} Formatted percentage string (e.g., "75%")
 */
const formatPercentage = (decimal) => {
  return `${Math.round(decimal * 100)}%`;
};

/**
 * Format a decimal as a percentage change with sign
 * @param {number} decimal - Decimal value (e.g., 0.75 or -0.25)
 * @returns {string} Formatted percentage change string (e.g., "+75%" or "-25%")
 */
const formatPercentChange = (decimal) => {
  if (decimal === undefined || decimal === null) return "";
  const sign = decimal >= 0 ? "+" : "";
  return `${sign}${Math.round(decimal * 100)}%`;
};

const Analytics = () => {
  const today = DateTime.now();
  const defaultStartDate = today.minus({ days: 7 }).toJSDate();
  const defaultEndDate = today.toJSDate();
  const { selectedClient: selectedClientId, isPlatformUser, platformClientId } =
    useContext(ClientContext);

  const [startDate, setStartDate] = useState(defaultStartDate);
  const [endDate, setEndDate] = useState(defaultEndDate);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(false);

  const dateFilter = {
    startDate,
    endDate,
    setStartDate,
    setEndDate,
  };

  // Format dates for API request
  const formatDateForAPI = (date) => {
    return DateTime.fromJSDate(date).toFormat("yyyy-MM-dd");
  };

  // Format dates for display in a human-readable format
  const formatDateForDisplay = (date) => {
    return DateTime.fromJSDate(date).toFormat("LLL d, yyyy");
  };

  useEffect(() => {
    const fetchMetrics = async () => {
      setLoading(true);

      const formattedStartDate = formatDateForAPI(startDate);
      const formattedEndDate = formatDateForAPI(endDate);

      // Use platform metrics for platform users, client metrics for regular users
      let metricsResponse, metricsError;
      if (selectedClientId === platformClientId) {
        // If viewing Analytics on the platform client, show consolidated platform metrics
        [metricsResponse, metricsError] = await getPlatformMetrics(
          platformClientId,
          formattedStartDate,
          formattedEndDate
        );
      } else {
        // If viewing Analytics on a destination client, show client-specific metrics
        [metricsResponse, metricsError] = await getClientMetrics(
          selectedClientId,
          formattedStartDate,
          formattedEndDate
        );
      }

      setLoading(false);
      if (metricsError) {
        setMetrics([]);
        toast.error(get(metricsError, "message", "Failed to load metrics."));
      } else {
        // Platform metrics response nests the array under metrics.clientMetrics;
        // client metrics response puts the array directly under metrics.
        const clientMetrics =
          selectedClientId === platformClientId
            ? get(metricsResponse, "metrics.clientMetrics", [])
            : get(metricsResponse, "metrics", []);
        setMetrics(clientMetrics);
      }
    };

    fetchMetrics();
  }, [startDate, endDate, selectedClientId, isPlatformUser, platformClientId]);

  /**
   * Transform the daily counts data into the format required by the Recharts LineChart
   * - Aggregates data from all metrics objects
   * - Converts object format to array of objects
   * - Sorts by date chronologically
   */
  const chartData = useMemo(() => {
    if (!metrics || !Array.isArray(metrics) || metrics.length === 0) return [];

    // Aggregate daily counts from all metrics objects
    const aggregatedDailyCounts = metrics.reduce((acc, metric) => {
      if (!metric.dailyCounts) return acc;

      Object.entries(metric.dailyCounts).forEach(([date, count]) => {
        acc[date] = (acc[date] || 0) + count;
      });

      return acc;
    }, {});

    return Object.entries(aggregatedDailyCounts)
      .map(([date, count]) => ({
        date,
        "Completed Sessions": count,
      }))
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [metrics]);

  // Calculate aggregated metrics for the cards
  const aggregatedMetrics = useMemo(() => {
    if (!metrics || !Array.isArray(metrics) || metrics.length === 0)
      return null;

    return metrics.reduce(
      (acc, metric) => ({
        total: (acc.total || 0) + (metric.total || 0),
        opened: (acc.opened || 0) + (metric.opened || 0),
        completed: (acc.completed || 0) + (metric.completed || 0),
        completedNonUnique:
          (acc.completedNonUnique || 0) + (metric.completedNonUnique || 0),
        conversion: (acc.conversion || 0) + (metric.conversion || 0),
        percentChange: (acc.percentChange || 0) + (metric.percentChange || 0),
      }),
      {}
    );
  }, [metrics]);

  // Determine if we should show the platform table view
  const showPlatformTable = selectedClientId === platformClientId;

  // Platform users viewing their platform client see the detailed table
  if (showPlatformTable) {
    return (
      <div className="flex flex-col flex-1 h-full px-12 gap-y-8">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold">Platform Analytics</h1>
        </div>
        <PlatformAnalyticsTable
          rows={metrics || []}
          isLoading={loading}
          dateFilter={dateFilter}
        />
      </div>
    );
  }

  // Regular users or platform users viewing a destination client see the summary view
  return (
    <div className="flex flex-col flex-1 h-full px-12 gap-y-8">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Analytics</h1>
        <div className="flex gap-x-2">
          <DateRangePicker dateFilter={dateFilter} />
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <LoadingSpinner />
        </div>
      ) : (
        aggregatedMetrics && (
          <>
            {/* Metrics Cards */}
            <div className="flex flex-wrap justify-between gap-4 mb-6">
              <div className="flex-1 min-w-[240px]">
                <MetricCard
                  title="Unique Sessions"
                  value={formatNumber(aggregatedMetrics.total)}
                  subtitle="Total unique Ignition sessions"
                />
              </div>
              <div className="flex-1 min-w-[240px]">
                <MetricCard
                  title="Opened Sessions"
                  value={formatNumber(aggregatedMetrics.opened)}
                  subtitle="All the opened Ignition sessions"
                />
              </div>
              <div className="flex-1 min-w-[240px]">
                <MetricCard
                  title="Total Completed Sessions"
                  value={formatNumber(aggregatedMetrics.completedNonUnique)}
                  subtitle="All completed Ignition sessions"
                  percentChange={
                    aggregatedMetrics.percentChange / metrics.length
                  }
                />
              </div>
              <div className="flex-1 min-w-[240px]">
                <MetricCard
                  title="Conversion Rate"
                  value={formatPercentage(
                    aggregatedMetrics.conversion / metrics.length
                  )}
                  subtitle="Conversion rate of opened to completed sessions"
                />
              </div>
            </div>

            {/* Chart */}
            <div className="rounded w-full">
              <h3 className="text-lg font-medium">
                Total Completed Sessions History
              </h3>
              <p className="text-sm text-gray-600">
                Daily completed sessions from {formatDateForDisplay(startDate)}{" "}
                to {formatDateForDisplay(endDate)}
              </p>
              <div className="mt-6 h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={chartData}
                    margin={{ top: 5, right: 30, left: 15, bottom: 5 }}
                  >
                    <CartesianGrid
                      strokeDasharray=""
                      horizontal={true}
                      vertical={false}
                    />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 12 }}
                      tickFormatter={(dateStr) => {
                        const date = DateTime.fromISO(dateStr);
                        return date.toFormat("MM/dd");
                      }}
                    />
                    <YAxis
                      width={48}
                      tick={{ fontSize: 12 }}
                      tickFormatter={(value) => value.toLocaleString()}
                    />
                    <Tooltip
                      formatter={(value) => [
                        value.toLocaleString(),
                        "Completed Sessions",
                      ]}
                      labelFormatter={(dateStr) => {
                        const date = DateTime.fromISO(dateStr);
                        return date.toFormat("MMM dd, yyyy");
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="Completed Sessions"
                      stroke="#10b981"
                      strokeWidth={2}
                      activeDot={{ r: 8 }}
                      animationDuration={750}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </>
        )
      )}
    </div>
  );
};

export default Analytics;
